import { useState, useMemo } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import WibizInternalLock from "@/components/WibizInternalLock";
import { toast } from "sonner";
import {
  ShieldAlert, DollarSign, TrendingUp, Settings2, Loader2, Phone, Mic, Cpu, Pencil,
  FileText, Printer, ExternalLink, Lock, History, MessageSquare,
} from "lucide-react";
import {
  ComposedChart, Area, XAxis, YAxis, Tooltip as RechartsTooltip, Legend,
  ResponsiveContainer, CartesianGrid,
} from "recharts";

// Same identity colors as VoiceCommandCenter.tsx's Twilio/Wibiz Voice chart —
// kept consistent across both pages for the same two cost entities.
const TWILIO_COLOR = "#f59e0b";
const TWILIO_FILL = "#fef3c7";
const EL_COLOR = "#84ab4a";
const EL_FILL = "#f0f7e8";
const LLM_COLOR = "#6366f1";
const LLM_FILL = "#eef2ff";

const PROVIDER_LABELS: Record<string, string> = {
  forge: "Forge",
  deepinfra: "DeepInfra",
  groq: "Groq",
  openai: "OpenAI",
  google: "Google",
};

function fmtUsd(n: number): string {
  return `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtMonth(m: string): string {
  const [y, mo] = m.split("-");
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

function fmtRate(n: number): string {
  return `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

function dueDateFromIssue(iso: string): string {
  const d = new Date(iso);
  d.setDate(d.getDate() + 14);
  return d.toISOString();
}

// Must match the real Wibiz Stripe invoice address exactly (Singapore
// entity) — never the older Malaysia address that appears on some historical
// example PDFs.
const WIBIZ_ADDRESS = {
  name: "WIBIZ",
  lines: ["111 North Bridge Road, #07-09,", "Peninsula Plaza, Singapore 179098"],
  email: "hello@wibiz.ai",
};

function StatTile({ icon: Icon, label, value, tone }: { icon: any; label: string; value: string; tone: "neutral" | "warning" | "ok" }) {
  const toneClasses = {
    neutral: "border-slate-800 bg-slate-900",
    warning: "border-red-900/50 bg-red-950/30",
    ok: "border-emerald-900/50 bg-emerald-950/20",
  }[tone];
  const iconTone = { neutral: "text-slate-500", warning: "text-red-500", ok: "text-emerald-500" }[tone];
  return (
    <Card className={`${toneClasses} shadow-none`}>
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-2">
          <Icon className={`w-4 h-4 ${iconTone}`} />
          <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</span>
        </div>
        <div className="text-2xl font-bold text-white tabular-nums">{value}</div>
      </CardContent>
    </Card>
  );
}

// BizConnect360's Pro plan has NO included/free Wibiz Voice minutes — pure
// usage billing from minute one, at whatever rate is currently active
// (versioned — see setWibizVoiceRate in server/db.ts, mirrors QuotaEditor's
// own pattern below exactly). This is now the PRIMARY pricing control for
// Wibiz Voice; the legacy QuotaEditor further down no longer drives any
// billed figure for voice, since there's no quota to set.
function WibizVoiceRateEditor({ currentRate, currentMarkupPct, onSaved }: { currentRate: number | null; currentMarkupPct: number | null; onSaved: () => void }) {
  const [editing, setEditing] = useState(false);
  const [rate, setRate] = useState(currentRate != null ? String(currentRate) : "0.08");
  const [markupPct, setMarkupPct] = useState(currentMarkupPct != null ? String(currentMarkupPct) : "50");
  const [notes, setNotes] = useState("");

  const setRateMutation = trpc.wibizInternal.setWibizVoiceRate.useMutation({
    onSuccess: () => {
      toast.success("Wibiz Voice rate updated.");
      setEditing(false);
      setNotes("");
      onSaved();
    },
    onError: (err) => toast.error(err.message || "Failed to update rate"),
  });

  const rateNum = Number(rate) || 0;
  const markupNum = Number(markupPct) || 0;
  const effectiveRate = rateNum * (1 + markupNum / 100);

  if (!editing) {
    return (
      <Button variant="outline" size="sm" className="border-slate-700 text-slate-300 hover:text-white" onClick={() => setEditing(true)}>
        <Settings2 className="w-3.5 h-3.5 mr-1.5" />
        {currentRate != null ? "Update Wibiz Voice rate" : "Set Wibiz Voice rate"}
      </Button>
    );
  }

  return (
    <div className="flex flex-wrap items-end gap-3 p-4 border border-slate-800 rounded-lg bg-slate-900/60">
      <div className="space-y-1">
        <Label className="text-xs text-slate-500">Base $ per connected minute (Wibiz's own cost)</Label>
        <Input
          type="number"
          min="0"
          step="0.0001"
          value={rate}
          onChange={(e) => setRate(e.target.value)}
          className="w-40 bg-slate-800 border-slate-700 text-white"
          placeholder="0.08"
        />
      </div>
      <div className="space-y-1">
        <Label className="text-xs text-slate-500">Markup %</Label>
        <Input
          type="number"
          min="0"
          step="1"
          value={markupPct}
          onChange={(e) => setMarkupPct(e.target.value)}
          className="w-28 bg-slate-800 border-slate-700 text-white"
          placeholder="50"
        />
      </div>
      <div className="space-y-1">
        <Label className="text-xs text-slate-500">Billable rate (what BC360 pays)</Label>
        <div className="w-32 h-9 flex items-center px-3 rounded-md border border-slate-700 bg-slate-800 text-white text-sm font-mono">
          ${effectiveRate.toFixed(4)}
        </div>
      </div>
      <div className="space-y-1 flex-1 min-w-[200px]">
        <Label className="text-xs text-slate-500">Notes (optional)</Label>
        <Input
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="bg-slate-800 border-slate-700 text-white"
          placeholder="e.g. Pro plan rate card update, effective 2026-07-23"
        />
      </div>
      <div className="flex gap-2">
        <Button
          size="sm"
          disabled={!rate.trim() || setRateMutation.isPending}
          onClick={() => setRateMutation.mutate({ ratePerMinuteUsd: Number(rate), markupPct: Number(markupPct), notes: notes.trim() || undefined })}
        >
          {setRateMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save"}
        </Button>
        <Button size="sm" variant="ghost" className="text-slate-400" onClick={() => setEditing(false)}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

// AI conversations (chatbot, TEXT only) — deliberately separate control from
// WibizVoiceRateEditor above: voice bills per MINUTE, conversations would
// bill per CONVERSATION. No rate is confirmed yet (pending "Sir Nick"), so
// currentRate is null until someone sets one here — the placeholder/default
// input value is intentionally left BLANK (not pre-filled with a guessed
// number) to avoid nudging toward an invented figure.
function AiConversationRateEditor({ currentRate, onSaved }: { currentRate: number | null; onSaved: () => void }) {
  const [editing, setEditing] = useState(false);
  const [rate, setRate] = useState(currentRate != null ? String(currentRate) : "");
  const [notes, setNotes] = useState("");

  const setRateMutation = trpc.wibizInternal.setAiConversationRate.useMutation({
    onSuccess: () => {
      toast.success("AI conversation rate updated.");
      setEditing(false);
      setNotes("");
      onSaved();
    },
    onError: (err) => toast.error(err.message || "Failed to update rate"),
  });

  if (!editing) {
    return (
      <Button variant="outline" size="sm" className="border-slate-700 text-slate-300 hover:text-white" onClick={() => setEditing(true)}>
        <Settings2 className="w-3.5 h-3.5 mr-1.5" />
        {currentRate != null ? "Update AI conversation rate" : "Set AI conversation rate"}
      </Button>
    );
  }

  return (
    <div className="flex flex-wrap items-end gap-3 p-4 border border-slate-800 rounded-lg bg-slate-900/60">
      <div className="space-y-1">
        <Label className="text-xs text-slate-500">$ per conversation</Label>
        <Input
          type="number"
          min="0"
          step="0.0001"
          value={rate}
          onChange={(e) => setRate(e.target.value)}
          className="w-32 bg-slate-800 border-slate-700 text-white"
          placeholder="not set"
        />
      </div>
      <div className="space-y-1 flex-1 min-w-[200px]">
        <Label className="text-xs text-slate-500">Notes (optional)</Label>
        <Input
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="bg-slate-800 border-slate-700 text-white"
          placeholder="e.g. confirmed by Sir Nick, effective 2026-08-01"
        />
      </div>
      <div className="flex gap-2">
        <Button
          size="sm"
          disabled={!rate.trim() || setRateMutation.isPending}
          onClick={() => setRateMutation.mutate({ ratePerConversationUsd: Number(rate), notes: notes.trim() || undefined })}
        >
          {setRateMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save"}
        </Button>
        <Button size="sm" variant="ghost" className="text-slate-400" onClick={() => setEditing(false)}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

// Fully separate section from the Wibiz Voice cards above — deliberately not
// merged into the same chart/table, per the 2026-07-21 ask. Tracks real,
// distinct chatbot (text) conversation counts immediately; cost only shows
// once a rate is confirmed and entered (never a guessed number).
function AiConversationsSection() {
  const utils = trpc.useUtils();
  const conversationsQuery = trpc.wibizInternal.monthlyChatbotConversations.useQuery({ months: 12 });
  const rateQuery = trpc.wibizInternal.activeAiConversationRate.useQuery();
  const rows = conversationsQuery.data ?? [];

  const totalConversationsYtd = useMemo(() => rows.reduce((sum, r) => sum + r.conversationCount, 0), [rows]);
  const totalCostYtd = useMemo(() => {
    if (rows.some(r => r.allocatedCost === null)) return null; // any unpriced month makes a YTD total meaningless
    return rows.reduce((sum, r) => sum + (r.allocatedCost ?? 0), 0);
  }, [rows]);

  const refresh = () => {
    utils.wibizInternal.monthlyChatbotConversations.invalidate();
    utils.wibizInternal.activeAiConversationRate.invalidate();
  };

  return (
    <Card className="border-slate-800 bg-slate-900 shadow-none mb-8">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold text-slate-300 flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-slate-500" />
          AI Conversations (chatbot — text only)
        </CardTitle>
        <p className="text-xs text-slate-600 pt-1">
          Separate from Wibiz Voice above — voice bills per connected minute; conversations would bill per
          conversation, at whatever rate is confirmed. Not yet priced — tracking only until confirmed.
        </p>
        <p className="text-xs text-amber-500/80 pt-1">
          Scope note: currently counts Dawn's associate-dashboard text conversations only (grace_chat_history).
          Grace's public chat widget doesn't persist conversation history yet, so it isn't included in this count.
        </p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <StatTile
            icon={MessageSquare}
            label="Total conversations (12mo)"
            value={totalConversationsYtd.toLocaleString("en-US")}
            tone="neutral"
          />
          <StatTile
            icon={DollarSign}
            label="Total estimated cost (12mo)"
            value={totalCostYtd !== null ? fmtUsd(totalCostYtd) : "Not yet priced"}
            tone={totalCostYtd !== null ? "neutral" : "warning"}
          />
        </div>

        <div className="mb-6">
          <AiConversationRateEditor currentRate={rateQuery.data?.ratePerConversationUsd ?? null} onSaved={refresh} />
          {rateQuery.data?.notes && (
            <p className="text-xs text-slate-600 mt-2">Note: {rateQuery.data.notes}</p>
          )}
        </div>

        <div className="overflow-x-auto">
          {conversationsQuery.isLoading ? (
            <div className="h-[100px] flex items-center justify-center text-slate-600 text-sm">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading...
            </div>
          ) : rows.length === 0 ? (
            <div className="h-[100px] flex items-center justify-center text-slate-600 text-sm">
              No chatbot conversation data yet.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500 uppercase tracking-wide border-b border-slate-800">
                  <th className="py-2 pr-4 font-medium">Month</th>
                  <th className="py-2 pr-4 font-medium text-right">Conversations</th>
                  <th className="py-2 pr-4 font-medium text-right">Rate/conversation</th>
                  <th className="py-2 font-medium text-right">Cost</th>
                </tr>
              </thead>
              <tbody className="tabular-nums">
                {[...rows].reverse().map(r => (
                  <tr key={r.month} className="border-b border-slate-900">
                    <td className="py-2 pr-4 text-slate-300">{fmtMonth(r.month)}</td>
                    <td className="py-2 pr-4 text-right text-slate-400">{r.conversationCount.toLocaleString("en-US")}</td>
                    <td className="py-2 pr-4 text-right text-slate-500">{r.ratePerConversationUsd !== null ? fmtRate(r.ratePerConversationUsd) : "—"}</td>
                    <td className="py-2 text-right font-medium text-slate-200">{r.allocatedCost !== null ? fmtUsd(r.allocatedCost) : "Not yet priced"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

type LlmPricingRowData = {
  id: number;
  provider: string;
  model: string;
  basePricePerMillionInput: number;
  basePricePerMillionOutput: number;
  markupPct: number;
  effectiveInputPricePerMillion: number;
  effectiveOutputPricePerMillion: number;
};

function LlmPricingRow({ row, onSaved }: { row: LlmPricingRowData; onSaved: () => void }) {
  const [editing, setEditing] = useState(false);
  const [baseIn, setBaseIn] = useState(String(row.basePricePerMillionInput));
  const [baseOut, setBaseOut] = useState(String(row.basePricePerMillionOutput));
  const [markup, setMarkup] = useState(String(row.markupPct));
  const [notes, setNotes] = useState("");

  const setPricingMutation = trpc.wibizInternal.setLlmPricing.useMutation({
    onSuccess: () => {
      toast.success(`${PROVIDER_LABELS[row.provider] ?? row.provider} pricing updated.`);
      setEditing(false);
      setNotes("");
      onSaved();
    },
    onError: (err) => toast.error(err.message || "Failed to update pricing"),
  });

  if (!editing) {
    return (
      <tr className="border-b border-slate-900">
        <td className="py-2 pr-4 text-slate-300">{PROVIDER_LABELS[row.provider] ?? row.provider}</td>
        <td className="py-2 pr-4 text-slate-500 font-mono text-xs">{row.model}</td>
        <td className="py-2 pr-4 text-right text-slate-400">{fmtRate(row.basePricePerMillionInput)}</td>
        <td className="py-2 pr-4 text-right text-slate-400">{fmtRate(row.basePricePerMillionOutput)}</td>
        <td className="py-2 pr-4 text-right text-slate-400">{row.markupPct}%</td>
        <td className="py-2 pr-4 text-right text-slate-200 font-medium">{fmtRate(row.effectiveInputPricePerMillion)}</td>
        <td className="py-2 pr-4 text-right text-slate-200 font-medium">{fmtRate(row.effectiveOutputPricePerMillion)}</td>
        <td className="py-2 text-right">
          <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white h-7 px-2" onClick={() => setEditing(true)}>
            <Pencil className="w-3.5 h-3.5" />
          </Button>
        </td>
      </tr>
    );
  }

  return (
    <tr className="border-b border-slate-900 bg-slate-900/60">
      <td className="py-3 pr-4 text-slate-300" colSpan={2}>
        {PROVIDER_LABELS[row.provider] ?? row.provider}
        <div className="text-xs text-slate-600 font-mono">{row.model}</div>
      </td>
      <td className="py-3 pr-4" colSpan={2}>
        <div className="flex gap-2">
          <div className="space-y-1">
            <Label className="text-[10px] text-slate-500">Base input $/1M</Label>
            <Input type="number" min="0" step="0.0001" value={baseIn} onChange={(e) => setBaseIn(e.target.value)} className="w-24 h-8 bg-slate-800 border-slate-700 text-white text-xs" />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] text-slate-500">Base output $/1M</Label>
            <Input type="number" min="0" step="0.0001" value={baseOut} onChange={(e) => setBaseOut(e.target.value)} className="w-24 h-8 bg-slate-800 border-slate-700 text-white text-xs" />
          </div>
        </div>
      </td>
      <td className="py-3 pr-4">
        <div className="space-y-1">
          <Label className="text-[10px] text-slate-500">Markup %</Label>
          <Input type="number" min="0" step="0.5" value={markup} onChange={(e) => setMarkup(e.target.value)} className="w-20 h-8 bg-slate-800 border-slate-700 text-white text-xs" />
        </div>
      </td>
      <td className="py-3 pr-4 text-right text-slate-600 text-xs" colSpan={2}>
        (effective price recalculates on save)
      </td>
      <td className="py-3">
        <div className="flex flex-col gap-1.5 items-end">
          <Input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Notes (optional)"
            className="w-40 h-8 bg-slate-800 border-slate-700 text-white text-xs"
          />
          <div className="flex gap-1.5">
            <Button
              size="sm"
              className="h-7 px-2 text-xs"
              disabled={!baseIn.trim() || !baseOut.trim() || !markup.trim() || setPricingMutation.isPending}
              onClick={() => setPricingMutation.mutate({
                provider: row.provider as any,
                model: row.model,
                basePricePerMillionInput: Number(baseIn),
                basePricePerMillionOutput: Number(baseOut),
                markupPct: Number(markup),
                notes: notes.trim() || undefined,
              })}
            >
              {setPricingMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save"}
            </Button>
            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-slate-400" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </div>
      </td>
    </tr>
  );
}

function LlmPricingSection() {
  const utils = trpc.useUtils();
  const pricingQuery = trpc.wibizInternal.llmPricingConfig.useQuery();
  const rows = pricingQuery.data ?? [];

  return (
    <Card className="border-slate-800 bg-slate-900 shadow-none mb-8">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold text-slate-300 flex items-center gap-2">
          <Cpu className="w-4 h-4 text-slate-500" />
          LLM pricing &amp; markup
        </CardTitle>
        <p className="text-xs text-slate-600 pt-1">
          Public list price by default, markup 0% until approved. Editing here saves a new version — past months'
          cost stays frozen against whatever pricing was active at the time, so history never silently changes.
        </p>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        {pricingQuery.isLoading ? (
          <div className="h-[120px] flex items-center justify-center text-slate-600 text-sm">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading...
          </div>
        ) : rows.length === 0 ? (
          <div className="h-[120px] flex items-center justify-center text-slate-600 text-sm">
            No pricing configured yet.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-500 uppercase tracking-wide border-b border-slate-800">
                <th className="py-2 pr-4 font-medium">Provider</th>
                <th className="py-2 pr-4 font-medium">Model</th>
                <th className="py-2 pr-4 font-medium text-right">Base in $/1M</th>
                <th className="py-2 pr-4 font-medium text-right">Base out $/1M</th>
                <th className="py-2 pr-4 font-medium text-right">Markup</th>
                <th className="py-2 pr-4 font-medium text-right">Eff. in $/1M</th>
                <th className="py-2 pr-4 font-medium text-right">Eff. out $/1M</th>
                <th className="py-2 font-medium text-right"></th>
              </tr>
            </thead>
            <tbody className="tabular-nums">
              {rows.map(r => (
                <LlmPricingRow key={`${r.provider}-${r.model}`} row={r} onSaved={() => utils.wibizInternal.llmPricingConfig.invalidate()} />
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Overage Invoices ───────────────────────────────────────────────────────
// Generates a Stripe-styled invoice document straight from the numbers
// already shown above (getMonthlyVoiceCostOverage) — never recalculated
// independently, so it can never disagree with the page. Sending to Stripe
// is a separate, explicitly-gated action (see InvoiceSettingsPanel) that
// stays off until someone turns it on.

type InvoiceLineItem = { description: string; periodLabel: string; qty: number; unitPriceUsd: number; amountUsd: number };
type InvoiceData = {
  id: number;
  invoiceNumber: string;
  month: string;
  lineItems: InvoiceLineItem[];
  subtotalUsd: number;
  quotaCreditUsd: number;
  totalUsd: number;
  amountDueUsd: number;
  status: "draft" | "sent" | "paid" | "void";
  stripeInvoiceId: string | null;
  stripeHostedUrl: string | null;
  stripeInvoicePdfUrl: string | null;
  generatedBy: string;
  generatedAt: string;
};

function InvoiceDocument({ invoice, billToName, billToEmail }: { invoice: InvoiceData; billToName: string; billToEmail: string | null }) {
  const dueIso = dueDateFromIssue(invoice.generatedAt);
  return (
    <div id="wibiz-invoice-print" className="bg-white text-black rounded-md overflow-hidden">
      <div className="h-2 bg-[#0f172a]" />
      <div className="p-8">
        <div className="flex justify-between items-start mb-6">
          <h2 className="text-3xl font-bold text-black">Invoice</h2>
          <div className="text-2xl font-bold text-slate-400 tracking-wide">WIBIZ</div>
        </div>
        <div className="text-sm mb-6 space-y-0.5 text-black">
          <div><span className="font-semibold">Invoice number</span>&nbsp;&nbsp;{invoice.invoiceNumber}</div>
          <div><span className="font-semibold">Date of issue</span>&nbsp;&nbsp;{fmtDate(invoice.generatedAt)}</div>
          <div><span className="font-semibold">Date due</span>&nbsp;&nbsp;{fmtDate(dueIso)}</div>
        </div>
        <div className="grid grid-cols-2 gap-8 mb-8 text-sm text-black">
          <div>
            <div className="font-semibold mb-1">{WIBIZ_ADDRESS.name}</div>
            {WIBIZ_ADDRESS.lines.map(l => <div key={l}>{l}</div>)}
            <div>{WIBIZ_ADDRESS.email}</div>
          </div>
          <div>
            <div className="font-semibold mb-1">Bill to</div>
            <div>{billToName}</div>
            {billToEmail && <div>{billToEmail}</div>}
          </div>
        </div>
        <div className="text-lg font-bold mb-1 text-black">{fmtUsd(invoice.amountDueUsd)} USD due {fmtDate(dueIso)}</div>
        {invoice.stripeHostedUrl ? (
          <a href={invoice.stripeHostedUrl} target="_blank" rel="noreferrer" className="text-blue-600 text-sm underline">Pay online</a>
        ) : (
          <div className="text-xs text-slate-400 mb-2">(Draft — not yet sent via Stripe)</div>
        )}
        <table className="w-full text-sm mt-6">
          <thead>
            <tr className="text-left text-xs text-slate-500 border-b border-slate-300">
              <th className="py-2 font-normal">Description</th>
              <th className="py-2 font-normal text-right">Qty</th>
              <th className="py-2 font-normal text-right">Unit price</th>
              <th className="py-2 font-normal text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {invoice.lineItems.map((li, i) => (
              <tr key={i} className="border-b border-slate-100 align-top">
                <td className="py-3">
                  <div className="text-black">{li.description}</div>
                  <div className="text-xs text-slate-500">{li.periodLabel}</div>
                </td>
                <td className="py-3 text-right text-black">{li.qty}</td>
                <td className="py-3 text-right text-black">{fmtUsd(li.unitPriceUsd)}</td>
                <td className="py-3 text-right text-black">{fmtUsd(li.amountUsd)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex justify-end mt-2">
          <table className="text-sm w-64">
            <tbody>
              <tr><td className="py-1 text-slate-600">Subtotal</td><td className="py-1 text-right text-black">{fmtUsd(invoice.subtotalUsd)}</td></tr>
              {invoice.quotaCreditUsd > 0 && (
                <tr><td className="py-1 text-slate-600">Included monthly quota</td><td className="py-1 text-right text-black">-{fmtUsd(invoice.quotaCreditUsd)}</td></tr>
              )}
              <tr className="border-t border-slate-300"><td className="py-1 text-slate-600">Total</td><td className="py-1 text-right text-black">{fmtUsd(invoice.totalUsd)}</td></tr>
              <tr><td className="py-1 font-semibold text-black">Amount due</td><td className="py-1 text-right font-semibold text-black">{fmtUsd(invoice.amountDueUsd)} USD</td></tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function InvoiceDialog({
  invoice, open, onOpenChange, billToName, billToEmail, invoicingEnabled, hasStripeKey, onSent,
}: {
  invoice: InvoiceData | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  billToName: string;
  billToEmail: string | null;
  invoicingEnabled: boolean;
  hasStripeKey: boolean;
  onSent: () => void;
}) {
  const sendMutation = trpc.wibizInternal.sendInvoiceToStripe.useMutation({
    onSuccess: (result) => {
      toast.success("Invoice sent via Stripe.");
      onSent();
      if (result.hostedUrl) window.open(result.hostedUrl, "_blank");
    },
    onError: (err) => toast.error(err.message || "Failed to send invoice"),
  });

  if (!invoice) return null;
  const canSend = invoicingEnabled && hasStripeKey && invoice.status === "draft";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Hides everything except the invoice document when printing/saving as PDF —
          Radix Dialog renders in a portal, so a scoped print rule alone isn't enough. */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #wibiz-invoice-print, #wibiz-invoice-print * { visibility: visible; }
          #wibiz-invoice-print { position: fixed; left: 0; top: 0; width: 100%; }
        }
      `}</style>
      <DialogContent className="max-w-2xl bg-slate-950 border-slate-800 text-white max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white">Invoice preview</DialogTitle>
        </DialogHeader>
        <InvoiceDocument invoice={invoice} billToName={billToName} billToEmail={billToEmail} />
        <div className="flex items-center justify-between gap-3 pt-2">
          <div className="text-xs text-slate-500">
            {invoice.status === "sent" ? (
              <Badge variant="outline" className="border-emerald-700 text-emerald-400">Sent via Stripe</Badge>
            ) : (
              <span>Draft — preview only, not sent anywhere yet.</span>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="border-slate-700 text-slate-200" onClick={() => window.print()}>
              <Printer className="w-3.5 h-3.5 mr-1.5" /> Print / Save PDF
            </Button>
            {invoice.stripeHostedUrl ? (
              <Button size="sm" variant="outline" className="border-slate-700 text-slate-200" onClick={() => window.open(invoice.stripeHostedUrl!, "_blank")}>
                <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> View in Stripe
              </Button>
            ) : (
              <Button
                size="sm"
                disabled={!canSend || sendMutation.isPending}
                title={!invoicingEnabled ? "Stripe invoicing is not activated yet — see Invoice Settings below" : !hasStripeKey ? "No Stripe key configured yet" : undefined}
                onClick={() => sendMutation.mutate({ invoiceId: invoice.id })}
              >
                {sendMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Lock className="w-3.5 h-3.5 mr-1.5" />}
                {canSend ? "Send via Stripe" : "Send via Stripe (not activated)"}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function InvoiceSettingsPanel() {
  const utils = trpc.useUtils();
  const settingsQuery = trpc.wibizInternal.invoiceSettings.useQuery();
  const [editing, setEditing] = useState(false);
  const [stripeKey, setStripeKey] = useState("");
  const [billToName, setBillToName] = useState("");
  const [billToEmail, setBillToEmail] = useState("");
  const [enabled, setEnabled] = useState(false);

  const data = settingsQuery.data;

  const startEditing = () => {
    setBillToName(data?.billToName ?? "BizConnect360");
    setBillToEmail(data?.billToEmail ?? "");
    setEnabled(data?.invoicingEnabled ?? false);
    setStripeKey("");
    setEditing(true);
  };

  const saveMutation = trpc.wibizInternal.setInvoiceSettings.useMutation({
    onSuccess: () => {
      toast.success("Invoice settings updated.");
      setEditing(false);
      utils.wibizInternal.invoiceSettings.invalidate();
    },
    onError: (err) => toast.error(err.message || "Failed to update settings"),
  });

  return (
    <div className="border border-slate-800 rounded-lg p-4 bg-slate-900/40 mb-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-300">
          <Lock className="w-4 h-4 text-slate-500" />
          Invoice settings
        </div>
        {!editing && (
          <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white h-7" onClick={startEditing}>
            <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
          </Button>
        )}
      </div>
      {!editing ? (
        <div className="text-xs text-slate-500 space-y-1">
          <div>Bill to: <span className="text-slate-300">{data?.billToName ?? "BizConnect360"}</span>{data?.billToEmail ? ` · ${data.billToEmail}` : ""}</div>
          <div>Wibiz Stripe key: <span className="text-slate-300">{data?.hasStripeKey ? "configured" : "not configured"}</span></div>
          <div>
            Stripe sending:{" "}
            {data?.invoicingEnabled ? (
              <span className="text-emerald-500 font-medium">ACTIVE</span>
            ) : (
              <span className="text-amber-500 font-medium">Not activated</span>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Bill-to name</Label>
              <Input value={billToName} onChange={(e) => setBillToName(e.target.value)} className="bg-slate-800 border-slate-700 text-white h-8 text-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Bill-to email</Label>
              <Input value={billToEmail} onChange={(e) => setBillToEmail(e.target.value)} className="bg-slate-800 border-slate-700 text-white h-8 text-sm" placeholder="billing@bizconnect360.com" />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Wibiz Stripe secret key{data?.hasStripeKey ? " (leave blank to keep current)" : ""}</Label>
            <Input type="password" value={stripeKey} onChange={(e) => setStripeKey(e.target.value)} className="bg-slate-800 border-slate-700 text-white h-8 text-sm" placeholder="sk_live_..." />
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={enabled} onCheckedChange={setEnabled} />
            <span className="text-sm text-slate-300">Activate Stripe sending</span>
          </div>
          <p className="text-xs text-slate-600">
            Keep this OFF until pricing/markup is approved. Generating and previewing invoices always works regardless of this setting.
          </p>
          <div className="flex gap-2">
            <Button
              size="sm"
              disabled={saveMutation.isPending}
              onClick={() => saveMutation.mutate({
                billToName: billToName.trim() || undefined,
                billToEmail,
                invoicingEnabled: enabled,
                ...(stripeKey.trim() ? { stripeSecretKey: stripeKey.trim() } : {}),
              })}
            >
              {saveMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save"}
            </Button>
            <Button size="sm" variant="ghost" className="text-slate-400" onClick={() => setEditing(false)}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}

type ElBackfillResult = {
  agentLabel: "Grace" | "Dawn";
  fetched: number;
  imported: number;
  skippedExisting: number;
  oldestFoundIso: string | null;
  newestFoundIso: string | null;
};

// One-time historical import — pulls REAL call duration/usage Wibiz Voice
// already recorded on their own servers for every past Grace/Dawn
// conversation, filling the gap before our own local logging started.
// Idempotent: never overwrites an existing row, so it's safe to run more
// than once (e.g. to extend the window further back later). Real durationSecs
// per call is what the $/minute allocated-cost figures above are computed
// from — this backfill is what makes historical months accurate too, not
// just calls logged going forward.
function WibizVoiceBackfillPanel() {
  const utils = trpc.useUtils();
  const [monthsBack, setMonthsBack] = useState("4");
  const [results, setResults] = useState<ElBackfillResult[] | null>(null);

  const backfillMutation = trpc.wibizInternal.backfillElevenLabsHistory.useMutation({
    onSuccess: (data) => {
      setResults(data as ElBackfillResult[]);
      const totalImported = data.reduce((sum: number, r: any) => sum + r.imported, 0);
      toast.success(totalImported > 0 ? `Imported ${totalImported} historical Wibiz Voice call(s).` : "No new calls found — everything in range was already logged.");
      utils.wibizInternal.monthlyOverage.invalidate();
    },
    onError: (err) => toast.error(err.message || "Backfill failed"),
  });

  return (
    <div className="border border-slate-800 rounded-lg p-4 bg-slate-900/40 mb-4">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-300 mb-2">
        <History className="w-4 h-4 text-slate-500" />
        Backfill Wibiz Voice history
      </div>
      <p className="text-xs text-slate-600 mb-3">
        Pulls real historical call duration straight from Wibiz Voice's own conversation records for Grace + Dawn —
        not an estimate. Only fills gaps; never overwrites a call already logged. Twilio isn't included
        (BizConnect360 pays that directly).
      </p>
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">Months back</Label>
          <Input
            type="number"
            min="1"
            max="24"
            value={monthsBack}
            onChange={(e) => setMonthsBack(e.target.value)}
            className="w-24 h-8 bg-slate-800 border-slate-700 text-white text-sm"
          />
        </div>
        <Button
          size="sm"
          disabled={backfillMutation.isPending || !monthsBack.trim()}
          onClick={() => backfillMutation.mutate({ monthsBack: Number(monthsBack) })}
        >
          {backfillMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <History className="w-3.5 h-3.5 mr-1.5" />}
          Run backfill
        </Button>
      </div>
      {results && (
        <div className="mt-3 space-y-1.5 text-xs">
          {results.map(r => (
            <div key={r.agentLabel} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-slate-400">
              <span className="font-semibold text-slate-300 w-12">{r.agentLabel}</span>
              <span>{r.fetched} found</span>
              <span className="text-emerald-500">{r.imported} imported</span>
              <span>{r.skippedExisting} already logged</span>
              {r.oldestFoundIso && (
                <span className="text-slate-600">
                  {fmtDate(r.oldestFoundIso)} – {fmtDate(r.newestFoundIso ?? r.oldestFoundIso)}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function InvoicesSection() {
  const utils = trpc.useUtils();
  const invoicesQuery = trpc.wibizInternal.listInvoices.useQuery();
  const settingsQuery = trpc.wibizInternal.invoiceSettings.useQuery();
  const overageQuery = trpc.wibizInternal.monthlyOverage.useQuery({ months: 12 });
  const [previewInvoice, setPreviewInvoice] = useState<InvoiceData | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const generateMutation = trpc.wibizInternal.generateInvoice.useMutation({
    onSuccess: (invoice) => {
      toast.success(`Invoice ${invoice.invoiceNumber} generated.`);
      utils.wibizInternal.listInvoices.invalidate();
      setPreviewInvoice(invoice as InvoiceData);
      setDialogOpen(true);
    },
    onError: (err) => toast.error(err.message || "Failed to generate invoice"),
  });

  // Fixes drafts generated BEFORE a pricing change (e.g. the 50% Wibiz Voice
  // markup) — generateInvoice snapshots amounts once and never recalculates,
  // so a draft can silently go stale and disagree with the live AI & Voice
  // Cost page above until someone notices (client-reported 2026-07-23).
  const refreshMutation = trpc.wibizInternal.refreshDraftInvoice.useMutation({
    onSuccess: (invoice) => {
      toast.success(`Invoice ${invoice.invoiceNumber} refreshed to current pricing.`);
      utils.wibizInternal.listInvoices.invalidate();
    },
    onError: (err) => toast.error(err.message || "Failed to refresh invoice"),
  });

  const invoices = (invoicesQuery.data ?? []) as InvoiceData[];
  // Current live billable amount for each month, so a stale draft (generated
  // before a pricing change) can be flagged instead of silently disagreeing
  // with the AI & Voice Cost page above.
  const currentBillableByMonth = new Map((overageQuery.data ?? []).map(m => [m.month, m.elCostAllocated]));
  // Billable amount is Wibiz Voice's real allocated cost only (duration ×
  // rate, see buildInvoiceLineItems) — NOT the combined
  // totalCost/overageUsd shown in the chart/table above, which also
  // includes Twilio (BizConnect360 pays that directly) and LLM. No quota
  // credit applies — the Pro plan has no included minutes. Computed the
  // same way here purely so the button label isn't misleading; the invoice
  // itself is always the source of truth once generated.
  const elBillableMonths = (overageQuery.data ?? []).filter(m => m.elCostAllocated > 0);
  const settings = settingsQuery.data;

  return (
    <Card className="border-slate-800 bg-slate-900 shadow-none mb-8">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold text-slate-300 flex items-center gap-2">
          <FileText className="w-4 h-4 text-slate-500" />
          Overage invoices
        </CardTitle>
        <p className="text-xs text-slate-600 pt-1">
          Generates a Stripe-styled invoice from Wibiz Voice's real allocated cost only (connected minutes × the
          Pro-plan rate — no included minutes, no invented figures). Twilio isn't billed here (BizConnect360 pays
          that directly) and LLM cost isn't currently billed either. Sending to Stripe is a separate,
          explicitly-activated step (Invoice Settings below).
        </p>
      </CardHeader>
      <CardContent>
        <WibizVoiceBackfillPanel />
        <InvoiceSettingsPanel />

        {elBillableMonths.length > 0 && (
          <div className="mb-4">
            <div className="text-xs text-slate-500 mb-2">Months with Wibiz Voice usage — generate an invoice:</div>
            <div className="flex flex-wrap gap-2">
              {elBillableMonths.map(m => (
                <Button
                  key={m.month}
                  size="sm"
                  variant="outline"
                  className="border-slate-700 text-slate-300 hover:text-white h-7 text-xs"
                  disabled={generateMutation.isPending}
                  onClick={() => generateMutation.mutate({ month: m.month })}
                >
                  {generateMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin mr-1.5" /> : <FileText className="w-3 h-3 mr-1.5" />}
                  {fmtMonth(m.month)} — {fmtUsd(m.elCostAllocated)}
                </Button>
              ))}
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          {invoicesQuery.isLoading ? (
            <div className="h-[80px] flex items-center justify-center text-slate-600 text-sm">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading...
            </div>
          ) : invoices.length === 0 ? (
            <div className="h-[80px] flex items-center justify-center text-slate-600 text-sm">
              No invoices generated yet.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500 uppercase tracking-wide border-b border-slate-800">
                  <th className="py-2 pr-4 font-medium">Invoice #</th>
                  <th className="py-2 pr-4 font-medium">Month</th>
                  <th className="py-2 pr-4 font-medium text-right">Amount due</th>
                  <th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="tabular-nums">
                {invoices.map(inv => {
                  const currentBillable = currentBillableByMonth.get(inv.month);
                  const isStale = inv.status === "draft" && currentBillable !== undefined && Math.abs(currentBillable - inv.amountDueUsd) > 0.005;
                  return (
                    <tr key={inv.id} className="border-b border-slate-900">
                      <td className="py-2 pr-4 text-slate-300 font-mono text-xs">{inv.invoiceNumber}</td>
                      <td className="py-2 pr-4 text-slate-400">{fmtMonth(inv.month)}</td>
                      <td className="py-2 pr-4 text-right text-slate-200 font-medium">
                        {fmtUsd(inv.amountDueUsd)}
                        {isStale && (
                          <div className="text-[10px] text-amber-500 font-normal" title="Generated before the current pricing/markup — no longer matches the AI & Voice Cost page above">
                            stale — current: {fmtUsd(currentBillable!)}
                          </div>
                        )}
                      </td>
                      <td className="py-2 pr-4">
                        <Badge variant="outline" className={inv.status === "sent" ? "border-emerald-700 text-emerald-400" : "border-slate-700 text-slate-400"}>
                          {inv.status}
                        </Badge>
                      </td>
                      <td className="py-2 text-right space-x-1">
                        {isStale && (
                          <Button variant="outline" size="sm" className="h-7 px-2 text-xs border-amber-700 text-amber-400 hover:text-amber-300"
                            disabled={refreshMutation.isPending}
                            onClick={() => refreshMutation.mutate({ invoiceId: inv.id })}>
                            {refreshMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : "Refresh"}
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" className="h-7 px-2 text-slate-400 hover:text-white" onClick={() => { setPreviewInvoice(inv); setDialogOpen(true); }}>
                          View
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </CardContent>

      <InvoiceDialog
        invoice={previewInvoice}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        billToName={settings?.billToName ?? "BizConnect360"}
        billToEmail={settings?.billToEmail ?? null}
        invoicingEnabled={settings?.invoicingEnabled ?? false}
        hasStripeKey={settings?.hasStripeKey ?? false}
        onSent={() => utils.wibizInternal.listInvoices.invalidate()}
      />
    </Card>
  );
}

function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-900 border border-slate-700 rounded-lg p-3 text-xs">
      <div className="text-slate-400 mb-1.5 font-medium">{fmtMonth(label)}</div>
      {payload.map((p: any) => (
        <div key={p.dataKey} className="flex items-center justify-between gap-4 text-slate-200">
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
            {p.name}
          </span>
          <span className="tabular-nums font-medium">{fmtUsd(Number(p.value))}</span>
        </div>
      ))}
    </div>
  );
}

function AiCostsContent() {
  const utils = trpc.useUtils();
  const overageQuery = trpc.wibizInternal.monthlyOverage.useQuery({ months: 12 });
  const rateQuery = trpc.wibizInternal.activeWibizVoiceRate.useQuery();

  const rows = overageQuery.data ?? [];
  const currentMonth = useMemo(() => {
    const key = new Date().toISOString().slice(0, 7);
    return rows.find(r => r.month === key) ?? null;
  }, [rows]);

  // The two headline figures BizConnect360's Pro plan requires: real counted
  // operations (Wibiz Voice calls + LLM requests — never estimated) and the
  // real dollar amount to allocate/charge for them (connected minutes × the
  // Pro-plan rate, no included quota subtracted — plus real LLM cost).
  // Twilio is deliberately excluded — BizConnect360 pays that directly.
  const totalApiOperationsYtd = useMemo(() => rows.reduce((sum, r) => sum + r.totalApiOperations, 0), [rows]);
  const totalAllocatedCostYtd = useMemo(() => rows.reduce((sum, r) => sum + r.totalAllocatedCost, 0), [rows]);
  const totalElMinutesYtd = useMemo(() => rows.reduce((sum, r) => sum + r.elDurationMins, 0), [rows]);

  const refresh = () => {
    utils.wibizInternal.monthlyOverage.invalidate();
    utils.wibizInternal.activeWibizVoiceRate.invalidate();
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <div className="max-w-6xl mx-auto px-6 py-8">
        <header className="flex items-center gap-3 mb-1">
          <ShieldAlert className="w-5 h-5 text-red-500" />
          <h1 className="text-xl font-bold">AI &amp; Voice Cost — Wibiz Internal</h1>
        </header>
        <p className="text-sm text-slate-500 mb-8">
          Wibiz Voice (real per-minute cost from voice_call_logs, billed at BizConnect360's Pro-plan rate with no
          included minutes) plus all 5 LLM providers Grace/Dawn use (voice and text-chat). Twilio is tracked for
          visibility only — BizConnect360 pays that directly. Not visible to BizConnect360 staff.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <StatTile
            icon={Phone}
            label="Total API operations (12mo)"
            value={totalApiOperationsYtd.toLocaleString("en-US")}
            tone="neutral"
          />
          <StatTile
            icon={DollarSign}
            label="Total estimated allocated cost (12mo)"
            value={fmtUsd(totalAllocatedCostYtd)}
            tone="neutral"
          />
          <StatTile
            icon={TrendingUp}
            label="This month's Wibiz Voice cost"
            value={currentMonth ? fmtUsd(currentMonth.elCostAllocated) : "—"}
            tone="neutral"
          />
        </div>
        <p className="text-xs text-slate-600 mb-6">
          {totalElMinutesYtd.toLocaleString("en-US", { maximumFractionDigits: 1 })} total connected Wibiz Voice
          minutes over the last {rows.length || 12} month(s), at {rateQuery.data ? fmtRate(rateQuery.data.effectiveRatePerMinuteUsd) : "—"}/minute
          billable (current rate). "Operations" = Wibiz Voice calls + LLM requests, counted directly from
          voice_call_logs and llm_usage_logs — never estimated.
        </p>

        <div className="mb-8">
          <WibizVoiceRateEditor currentRate={rateQuery.data?.ratePerMinuteUsd ?? null} currentMarkupPct={rateQuery.data?.markupPct ?? null} onSaved={refresh} />
          {rateQuery.data?.notes && (
            <p className="text-xs text-slate-600 mt-2">Note: {rateQuery.data.notes}</p>
          )}
        </div>

        <LlmPricingSection />

        <Card className="border-slate-800 bg-slate-900 shadow-none mb-8">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-300 flex items-center gap-2">
              <Mic className="w-4 h-4 text-slate-500" />
              Monthly allocated cost
            </CardTitle>
            <p className="text-xs text-slate-600 pt-1">
              Wibiz Voice is real connected minutes × the Pro-plan rate in effect that month — no included quota is
              subtracted, since BizConnect360's plan has none.
            </p>
          </CardHeader>
          <CardContent>
            {overageQuery.isLoading ? (
              <div className="h-[220px] flex items-center justify-center text-slate-600 text-sm">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading...
              </div>
            ) : rows.length === 0 ? (
              <div className="h-[220px] flex items-center justify-center text-slate-600 text-sm">
                No voice call cost data yet.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <ComposedChart data={rows} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="month"
                    tickFormatter={fmtMonth}
                    tick={{ fontSize: 11, fill: "#64748b" }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    tickFormatter={(v: number) => `$${v}`}
                    tick={{ fontSize: 11, fill: "#64748b" }}
                    tickLine={false}
                    axisLine={false}
                    width={54}
                  />
                  <RechartsTooltip content={<CustomTooltip />} />
                  <Legend iconSize={8} wrapperStyle={{ fontSize: 11, paddingTop: 8, color: "#94a3b8" }} />
                  <Area type="monotone" dataKey="twilioCost" name="Twilio (not billed)" stroke={TWILIO_COLOR} fill={TWILIO_FILL} fillOpacity={0.15} strokeWidth={2} dot={false} stackId="cost" />
                  <Area type="monotone" dataKey="elCostAllocated" name="Wibiz Voice (allocated)" stroke={EL_COLOR} fill={EL_FILL} fillOpacity={0.15} strokeWidth={2} dot={false} stackId="cost" />
                  <Area type="monotone" dataKey="llmCost" name="LLM" stroke={LLM_COLOR} fill={LLM_FILL} fillOpacity={0.15} strokeWidth={2} dot={false} stackId="cost" />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-800 bg-slate-900 shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-300">Monthly breakdown</CardTitle>
            <p className="text-xs text-slate-600 pt-1">
              "Wibiz Voice mins" and "Ops" are real counted usage. "Allocated" is what's actually chargeable
              (minutes × that month's rate + LLM cost) — Twilio and Wibiz's own provider cost are shown for
              visibility only, never billed.
            </p>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500 uppercase tracking-wide border-b border-slate-800">
                  <th className="py-2 pr-4 font-medium">Month</th>
                  <th className="py-2 pr-4 font-medium text-right">Ops</th>
                  <th className="py-2 pr-4 font-medium text-right">Wibiz Voice mins</th>
                  <th className="py-2 pr-4 font-medium text-right" title="What BC360 is billed per connected minute this month">Rate/min (billable)</th>
                  <th className="py-2 pr-4 font-medium text-right">Twilio (n/b)</th>
                  <th className="py-2 pr-4 font-medium text-right">LLM cost</th>
                  <th className="py-2 pr-4 font-medium text-right">Wibiz provider cost</th>
                  <th className="py-2 font-medium text-right">Allocated cost</th>
                </tr>
              </thead>
              <tbody className="tabular-nums">
                {[...rows].reverse().map(r => (
                  <tr key={r.month} className="border-b border-slate-900">
                    <td className="py-2 pr-4 text-slate-300">{fmtMonth(r.month)}</td>
                    <td className="py-2 pr-4 text-right text-slate-400">{r.totalApiOperations.toLocaleString("en-US")}</td>
                    <td className="py-2 pr-4 text-right text-slate-400">{r.elDurationMins.toLocaleString("en-US", { maximumFractionDigits: 1 })}</td>
                    <td className="py-2 pr-4 text-right text-slate-500">{fmtRate(r.elEffectiveRatePerMinuteUsd)}</td>
                    <td className="py-2 pr-4 text-right text-slate-600">{fmtUsd(r.twilioCost)}</td>
                    <td className="py-2 pr-4 text-right text-slate-400">{fmtUsd(r.llmCost)}</td>
                    <td className="py-2 pr-4 text-right text-slate-500" title="Wibiz Voice's own self-reported cost — sparse/often incomplete, margin visibility only">{fmtUsd(r.elCostReported)}</td>
                    <td className="py-2 text-right font-medium text-slate-200">
                      {fmtUsd(r.totalAllocatedCost)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length > 0 && (
              <div className="mt-4 pt-4 border-t border-slate-800 flex items-center justify-between text-sm">
                <span className="text-slate-500">Total estimated allocated cost, last {rows.length} month(s)</span>
                <span className="font-bold tabular-nums text-white">
                  {fmtUsd(rows.reduce((sum, r) => sum + r.totalAllocatedCost, 0))}
                </span>
              </div>
            )}
          </CardContent>
        </Card>

        <AiConversationsSection />

        <InvoicesSection />
      </div>
    </div>
  );
}

export default function WibizInternalAiCosts() {
  return (
    <WibizInternalLock>
      <AiCostsContent />
    </WibizInternalLock>
  );
}
